import { useState, useRef, useEffect } from "react"
import { LuLoaderCircle } from "react-icons/lu"
import { FiEdit2, FiX } from "react-icons/fi"

const VARIANTS = {
    paragraph: {
        minHeight: "h-[20px]",
        text: "text-xs leading-tight text-secondary-foreground",
    },
    heading: {
        minHeight: "h-[25px]",
        text: "text-base font-semibold text-foreground",
    },
}

export default function AsyncEditableTextArea({
    value,
    onSave,
    loading = false,
    error = null,
    placeholder,
    disabled = false,
    rows = 1,
    title = "Double-click to edit",
    className = "",
    variant = "paragraph",
    textClassName,
    onEditingChange,
}) {
    const variantStyle = VARIANTS[variant] || VARIANTS.paragraph
    const textClasses = textClassName || variantStyle.text
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(value ?? "")
    const textareaRef = useRef(null)
    const skipBlurRef = useRef(false)
    const submittedRef = useRef(false)
    const wasLoadingRef = useRef(loading)

    const autoResize = () => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = "auto"
        el.style.height = `${el.scrollHeight}px`
    }

    useEffect(() => {
        if (editing && textareaRef.current) {
            textareaRef.current.focus()
            textareaRef.current.select()
        }
    }, [editing])

    useEffect(() => {
        if (editing) autoResize()
    }, [draft, editing])

    useEffect(() => {
        onEditingChange?.(editing)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing])


    useEffect(() => {
        const finishedLoading = wasLoadingRef.current && !loading
        wasLoadingRef.current = loading
        
        if (finishedLoading && submittedRef.current) {
            submittedRef.current = false
            if (!error) setEditing(false)
        }
    }, [loading, error])

    const startEdit = () => {
        if (disabled) return
        setDraft(value ?? "")
        setEditing(true)
    }

    const commit = () => {
        const trimmed = draft.trim()
        if (!trimmed || trimmed === value) {
            setEditing(false)
            return
        }
        submittedRef.current = true
        onSave?.(trimmed)
    }

    const cancel = () => {
        submittedRef.current = false
        setEditing(false)
        setDraft(value ?? "")
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            skipBlurRef.current = true
            commit()
        } else if (e.key === "Escape") {
            e.preventDefault()
            skipBlurRef.current = true
            cancel()
        }
    }

    const handleBlur = () => {
        if (skipBlurRef.current) {
            skipBlurRef.current = false
            return
        }

        if (loading) return
        commit()
    }

    const boxClasses = "w-full h-full box-border rounded-sm pl-1 pr-6 py-0.5 ring-1 break-words overflow-hidden"

    if (editing) {
        return (
            <div className={`relative w-full min-w-0 ${variantStyle.height} ${className} right-1`}>
                <div className='relative w-full min-w-0 h-full'>
                    <textarea
                        ref={textareaRef}
                        rows={rows}
                        value={draft}
                        placeholder={placeholder}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                        className={`${boxClasses} bg-card m-0 border-0 outline-none ring-primary disabled:opacity-70 resize-none ${textClasses}`}
                    />
                    <button
                        type='button'
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancel}
                        disabled={loading}
                        title='Cancel'
                        className='absolute top-0 right-0 flex items-center justify-center p-0.5 rounded-sm text-secondary-foreground/60 hover:text-secondary-foreground disabled:opacity-40 cursor-pointer'
                    >
                        <FiX className='w-3.5 h-3.5' />
                    </button>
                    {loading && (
                        <div className='absolute inset-0 flex items-start justify-end p-0 bg-card/60 rounded-sm'>
                            <LuLoaderCircle className='w-4 h-4 animate-spin text-secondary-foreground' />
                        </div>
                    )}
                </div>
                {error && !loading && (
                    <span className='block text-[11px] leading-tight text-destructive-foreground mt-1 break-words'>
                        {error}
                    </span>
                )}
            </div>
        )
    }

    return (
        <div className={`relative w-full min-w-0 group/editable ${variantStyle.height} ${className} right-1`}>
            <span
                className={`block ${boxClasses} ring-transparent cursor-text ${textClasses}`}
                onDoubleClick={startEdit}
                title={title}
            >
                {value || (
                    <span className='text-secondary-foreground/30 italic'>{placeholder}</span>
                )}
            </span>
            {!disabled && (
                <button
                    type='button'
                    onClick={startEdit}
                    title='Edit'
                    className='absolute top-0 right-0 hidden group-hover/editable:flex items-center justify-center p-0.5 rounded-sm bg-card/80 text-secondary-foreground/70 hover:text-secondary-foreground cursor-pointer'
                >
                    <FiEdit2 className='w-3 h-3' />
                </button>
            )}
            {error && (
                <span className='block text-[11px] leading-tight text-destructive-foreground mt-1 break-words'>
                    {error}
                </span>
            )}
        </div>
    )
}
